import { Box, Button, DialogActions, Stack, Typography } from '@mui/material';
import { ReactNode, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import FormField from 'components/common/formikForm/formField';
import FormWrapper from 'components/common/formikForm/formWrapper';
import SubmitButton from 'components/common/formikForm/SubmitButton';

import api from 'api';
import PERMISSION from 'assets/permissions.json';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { RootState } from 'redux/store';
import { ApplicantFormInterface, OptionInterface } from 'types';
import {
    applicantFormInitialValues,
    applicantFormValidationSchema,
} from 'utils/validations/onboarding';

interface Props {
    companyOptions: OptionInterface[];
    onClose: () => void;
    topContent?: ReactNode;
}

const ApplicantForm = ({ companyOptions, onClose, topContent }: Props) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { service_provider_id: serviceProviderId } = useSelector(
        (state: RootState) => state.user.entity
    );
    const { permissionsList } = useSelector((state: RootState) => state.permissions);
    const { translate } = useTranslate();
    const [disabled, setDisabled] = useState(false);
    const canEditOrganizationProfile = permissionsList.some(
        (permission) => permission?.name === PERMISSION.EDIT_ORGANIZATION_PROFILE
    );
    const initialValues = useMemo<ApplicantFormInterface>(
        () => ({
            ...applicantFormInitialValues,
            service_provider_id: canEditOrganizationProfile ? null : (serviceProviderId ?? null),
        }),
        [canEditOrganizationProfile, serviceProviderId]
    );
    const sectionLabelSx = {
        color: '#64748b',
        fontSize: '12px',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        mb: 1.25,
    };
    const formAreaSx = {
        '& .MuiTextField-root, & .MuiFormControl-root': {
            width: '100%',
        },
        '& .MuiInputLabel-root': {
            color: '#64748b',
            fontWeight: 500,
        },
        '& .MuiOutlinedInput-root': {
            borderRadius: '14px',
            backgroundColor: 'rgba(255, 255, 255, 0.82)',
            boxShadow: '0 1px 0 rgba(255, 255, 255, 0.85)',
            transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
            '& fieldset': {
                borderColor: 'rgba(148, 163, 184, 0.28)',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(131, 83, 226, 0.38)',
            },
            '&.Mui-focused': {
                boxShadow: '0 0 0 4px rgba(131, 83, 226, 0.08)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#8353e2',
            },
        },
        '& .MuiFormHelperText-root': {
            marginLeft: '4px',
        },
        '& .MuiFormControlLabel-root': {
            margin: 0,
            minHeight: '42px',
            alignItems: 'center',
        },
        '& .MuiCheckbox-root': {
            color: '#94a3b8',
        },
        '& .MuiCheckbox-root.Mui-checked': {
            color: '#8353e2',
        },
    };

    const handleSubmit = async (values: ApplicantFormInterface) => {
        try {
            setDisabled(true);
            const payload = {
                email: values.email,
                firstName: values.firstName,
                lastName: values.lastName,
                service_provider_id: values.service_provider_id,
                permissions: [
                    values.selfAssessment && 'EDIT PROFILE',
                    values.showResults && 'VIEW ASSESSMENT RESULTS',
                ].filter(Boolean),
            };

            const request = await api.externalClient.post(payload);
            if (request.status === 200 || request.status === 201) {
                dispatch(
                    setAlert({
                        message: translate('clientSuccessfullyAdded'),
                        type: 'success',
                    })
                );
                onClose();
                setTimeout(() => {
                    if (values.selfAssessment) {
                        navigate(`${request.data?.id}/client-profile/agreement`);
                    } else {
                        navigate('sales-onboarding/assessment');
                    }
                }, 2000);
            }
        } catch {
            dispatch(setAlert({ message: translate('somethingWentWrong'), type: 'error' }));
        } finally {
            setDisabled(false);
        }
    };

    return (
        <FormWrapper
            enableReinitialize
            initialValues={initialValues}
            validationSchema={applicantFormValidationSchema(translate)}
            onSubmit={handleSubmit}
        >
            <Box
                sx={{
                    px: { xs: 2.25, sm: 3 },
                    pt: 2,
                    pb: 0,
                    ...formAreaSx,
                }}
            >
                <Stack spacing={1.5}>
                    {topContent && <Box>{topContent}</Box>}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr',
                            gap: 1.5,
                        }}
                    >
                        {canEditOrganizationProfile && (
                            <Box>
                                {/* <Typography sx={sectionLabelSx}>{translate('serviceProvider')}</Typography> */}
                                <FormField
                                    required
                                    type="select"
                                    size="small"
                                    name="service_provider_id"
                                    label="organization"
                                    disabled={disabled}
                                    options={companyOptions}
                                />
                            </Box>
                        )}

                        <Box>
                            {/* <Typography sx={sectionLabelSx}>{translate('clientsFirstName')}</Typography> */}
                            <FormField
                                required
                                size="small"
                                name="firstName"
                                label="clientsFirstName"
                                disabled={disabled}
                            />
                        </Box>

                        <Box>
                            {/* <Typography sx={sectionLabelSx}>{translate('clientsLastName')}</Typography> */}
                            <FormField
                                required
                                size="small"
                                name="lastName"
                                label="clientsLastName"
                                disabled={disabled}
                            />
                        </Box>

                        <Box>
                            {/* <Typography sx={sectionLabelSx}>{translate('clientsEmail')}</Typography> */}
                            <FormField
                                required
                                size="small"
                                name="email"
                                label="clientsEmail"
                                disabled={disabled}
                            />
                        </Box>
                    </Box>

                    <Box>
                        <Typography sx={sectionLabelSx}>{translate('permissions')}</Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <FormField
                                type="checkbox"
                                size="small"
                                name="selfAssessment"
                                label="selfAssessment"
                                disabled={disabled}
                            />
                            <FormField
                                type="checkbox"
                                size="small"
                                name="showResults"
                                label="showResults"
                                disabled={disabled}
                            />
                        </Stack>
                    </Box>
                </Stack>
            </Box>
            <DialogActions
                sx={{
                    px: { xs: 2.25, sm: 3 },
                    py: 2.5,
                    mt: 1.5,
                    borderTop: '1px solid rgba(148, 163, 184, 0.18)',
                    background: 'rgba(255, 255, 255, 0.72)',
                    backdropFilter: 'blur(12px)',
                    justifyContent: 'flex-end',
                    gap: 1.5,
                }}
            >
                <Button
                    type="button"
                    onClick={onClose}
                    disabled={disabled}
                    sx={{
                        height: 40,
                        px: 2.25,
                        borderRadius: '12px',
                        border: '1px solid rgba(148, 163, 184, 0.35)',
                        color: '#475569',
                        backgroundColor: '#ffffff',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': {
                            borderColor: 'rgba(131, 83, 226, 0.28)',
                            backgroundColor: '#ffffff',
                            boxShadow: '0 10px 22px rgba(15, 23, 42, 0.08)',
                        },
                    }}
                >
                    {translate('cancel')}
                </Button>
                <SubmitButton
                    loading={disabled}
                    sx={{
                        height: 40,
                        px: 2.25,
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #8353e2 0%, #6d41d8 100%)',
                        boxShadow: '0 12px 26px rgba(131, 83, 226, 0.28)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #8353e2 0%, #6d41d8 100%)',
                            boxShadow: '0 16px 30px rgba(131, 83, 226, 0.32)',
                        },
                    }}
                >
                    save&Proceed
                </SubmitButton>
            </DialogActions>
        </FormWrapper>
    );
};

export default ApplicantForm;
